/* eslint-disable react/no-array-index-key */
import {
  ArrayUtils,
  ConfigObjectType,
  LoadingSpinner,
  omitNullsy,
  serializeJSONToString,
  useApiQuery,
  useLocalStorage,
  utils,
  NewMinimizedMaximized,
} from '@wallaby-ui/core';
import {
  DateFormatter,
  HospitalizationDetailTimelineAnnotationFormatter,
  HospitalizationDetailTimelineEventTypeFormatter,
  MerieSchema,
  LightTable,
  DynamicChunk,
  sortMedicationData,
  getEncounterEventGroupKey,
  useFormatters,
  getFormattedAnnotationString,
  getNonMedicationAnnotationText,
  TimelineAnnotation,
} from '@wallaby-ui/document';
// import timelineDataMock from '@wallaby-ui/merie/src/js/mocks/feature/hospitalization_detail/hospitalization_timeline_data_mock.json';
import classNames from 'classnames';
import {parseISO} from 'date-fns';
import React, {useCallback, useMemo} from 'react';
import {defineMessages, useIntl} from 'react-intl';

import {HospitalizationState} from './HospitalizationDetailLayoutV2';

export const I18N = defineMessages({
  DATE_TIME: {
    id: 'timeline.date',
    defaultMessage: 'Date',
    description: 'Date column for timeline',
  },
  EVENT_TYPE: {
    id: 'timeline.eventType',
    defaultMessage: 'Event type',
    description: 'Event type column for timeline',
  },
  ANNOTATION: {
    id: 'timeline.annotation',
    defaultMessage: 'Annotation',
    description: 'Annotation column for timeline',
  },
  DOCUMENT_NAME: {
    id: 'timeline.documentName',
    defaultMessage: 'Document name',
    description: 'Document name column for timeline',
  },
});

type EncounterTimelineDto = MerieSchema.components['schemas']['EncounterTimelineDto'];
export type TimelineRowType = EncounterTimelineDto['events'][0];
export type TimelineAnnotationMedication = TimelineRowType['annotations'][0]['medicationData'];

export function useTimelineData(config: ConfigObjectType, hospitalizationId: string, state: HospitalizationState) {
  const timelineApi = `${config.service.url.hospitalizationDetail}/${hospitalizationId}/timeline`;
  const timelineApiParams = omitNullsy({
    startDate: state.timeline.startDate,
    endDate: state.timeline.endDate,
    eventTypes: state.timeline.eventTypes,
    eventCategories: state.timeline.eventCategories,
    riskFactors: state.timeline.riskFactors,
    atcHierarchyIds: state.timeline.atcHierarchyIds,
    documentName: state.timeline.documentName,
  });
  const {
    data: timelineData,
    isLoading: timelineIsLoading,
    refetch: timelineRefetch,
  } = useApiQuery<EncounterTimelineDto>([timelineApi, serializeJSONToString(timelineApiParams)], null, {
    axiosConfig: {
      url: timelineApi,
      params: new URLSearchParams(timelineApiParams),
    },
  });
  const eventsWithSortedMedicationAnnotations: TimelineRowType[] = useMemo(() => {
    return (timelineData?.events ?? []).map((v) => {
      const isMedication = v.eventType === 'MEDICATION';
      const annotations = v.annotations ?? [];
      const result: TimelineRowType = {...v, annotations: isMedication ? sortMedicationData(annotations) : annotations};
      return result;
    });
  }, [timelineData]);
  const sortEventsDescending = (state.timelineSorting?.direction ?? 'desc') === 'desc';
  const sortedTimelineEvents: TimelineRowType[] = useMemo(() => {
    return ArrayUtils.sortedBy(
      eventsWithSortedMedicationAnnotations,
      (row) => {
        const encounterEventGroupKey = getEncounterEventGroupKey(row.eventType);
        const timestampString = parseISO(row.eventTimestamp ?? '');
        const timestamp = Number.isNaN(+timestampString) ? new Date(0) : timestampString;
        return [timestamp, encounterEventGroupKey];
      },
      [sortEventsDescending, sortEventsDescending]
    );
  }, [eventsWithSortedMedicationAnnotations, sortEventsDescending]);
  return {sortedTimelineEvents, timelineIsLoading, timelineRefetch};
}

type Props = {
  config: ConfigObjectType;
  defaultScroll: number;
  onScroll: (event: Event) => void;
  filters: {[key: string]: any};
  changeFilters: (data: any) => void;
  storageIdPrefix: string;
  style?: React.CSSProperties;
  versionSuffix?: string;
  encounterId: number | undefined;
  sortedTimelineEvents: TimelineRowType[];
  timelineEventAnnotationFilter?: string;
  timelineIsLoading: boolean;
  pharmaRecommendations: MerieSchema.components['schemas']['PharmaRecommendationsDto'] | undefined;
  maximized?: boolean;
  setMinimizedMaximized?: (newValues: NewMinimizedMaximized) => void;
};
export const HospitalizationDiagnosticsTimeline: React.FC<Props> = (props) => {
  const {
    config,
    defaultScroll,
    onScroll,
    filters,
    changeFilters,
    storageIdPrefix,
    versionSuffix = '',
    encounterId,
    style,
    sortedTimelineEvents,
    timelineEventAnnotationFilter,
    timelineIsLoading,
    pharmaRecommendations,
    maximized,
    setMinimizedMaximized,
  } = props;
  const intl = useIntl();

  const [timelineDefaultDynamicChunks, setTimelineDefaultDynamicChunks] = useLocalStorage(
    `${storageIdPrefix}.timelineDynamicChunks`,
    [] as DynamicChunk[]
  );
  const timelineDynamicChunksOnChange = useCallback(
    (newDynamicChunks: DynamicChunk[]) => {
      setTimelineDefaultDynamicChunks(newDynamicChunks);
    },
    [setTimelineDefaultDynamicChunks]
  );
  const formatters = useFormatters();

  const onPhRecAnnotationClicked = useCallback(() => {
    if (maximized && setMinimizedMaximized) {
      setMinimizedMaximized({maximized: false});
    }
  }, [maximized, setMinimizedMaximized]);

  // annotation text is composed on FE side -> filter on FE side
  const annotationTextFilteredSortedTimelineEvents = useMemo(
    () =>
      sortedTimelineEvents.flatMap((event) => {
        if (timelineEventAnnotationFilter == null) return [event];
        const getText =
          event.eventType === 'MEDICATION'
            ? (a) => getFormattedAnnotationString(a, formatters)
            : (a) => getNonMedicationAnnotationText(a, formatters.t) ?? '';

        const normalizedFilter = utils.StringUtils.normalize(timelineEventAnnotationFilter);
        const matchingAnnotations = event.annotations.filter((a) => {
          return utils.StringUtils.normalize(getText(a)).includes(normalizedFilter);
        });

        if (matchingAnnotations.length === 0) return [];
        return [{...event, annotations: matchingAnnotations}];
      }),
    [formatters, sortedTimelineEvents, timelineEventAnnotationFilter]
  );

  if (timelineIsLoading)
    return (
      <div
        className={classNames('dlLoadingSpinnerContainer', 'dlFlexBoxContainerInContainer', {dlLoadingSpinnerParent: true})}
        data-name={'loadingSpinnerParent'}
      >
        <LoadingSpinner isVisible marginTop={0} />
      </div>
    );

  return (
    <>
      <LightTable
        style={style}
        data={annotationTextFilteredSortedTimelineEvents}
        defaultScroll={{to: defaultScroll}}
        onScroll={onScroll}
        columns={[
          {
            field: 'date',
            title: utils.TranslateUtils.translate(I18N.DATE_TIME, intl),
            render: (row, compat) => <DateFormatter {...compat} value={row.eventDate} />,
            width: 95,
            sorting: true,
          },
          {
            field: 'eventType',
            title: utils.TranslateUtils.translate(I18N.EVENT_TYPE, intl),
            render: (row) => <HospitalizationDetailTimelineEventTypeFormatter value={row.eventType} />,
            width: 170,
          },
          {
            field: 'annotation',
            title: utils.TranslateUtils.translate(I18N.ANNOTATION, intl),
            flex: 3,
            minWidth: 180, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
            render: (row, compat) => (
              <HospitalizationDetailTimelineAnnotationFormatter
                {...compat}
                versionSuffix={versionSuffix}
                encounterId={encounterId}
                data={row}
                config={config}
                formatters={formatters}
                pharmaRecommendations={pharmaRecommendations}
                onPhRecAnnotationClicked={onPhRecAnnotationClicked}
              />
            ),
          },
          {
            field: 'documentName',
            title: utils.TranslateUtils.translate(I18N.DOCUMENT_NAME, intl),
            flex: 2,
            minWidth: 140, // PDH-1589: readable floor; below it the table scrolls instead of collapsing the column
            render: (row) => (
              <>
                {row.annotations.map((a, i) => (
                  <TimelineAnnotation
                    key={i}
                    versionSuffix={versionSuffix}
                    encounterId={encounterId}
                    documentVersionId={a.documentVersionId}
                    isDiscontinued={a.medicationData?.isDiscontinued}
                  >
                    {a.documentName ?? '\u00A0'}
                  </TimelineAnnotation>
                ))}
              </>
            ),
          },
        ]}
        controlledSort={filters.timelineSorting}
        onOrderChange={(field, direction) => {
          changeFilters({
            timelineSorting: field ? {field, direction} : null,
          });
        }}
        useDynamicRows
        rowsAreSingleLine
        defaultDynamicChunks={timelineDefaultDynamicChunks}
        dynamicChunksOnChange={timelineDynamicChunksOnChange}
      />
    </>
  );
};
HospitalizationDiagnosticsTimeline.displayName = 'HospitalizationDiagnosticsTimeline';
